import { useId, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ScaleButton,
  ScaleIconActionRefresh,
  ScaleIconAlertError,
  ScaleLogo,
} from '@cloudorder/ui';

import { errorPresentation } from './errorPresentation';
import styles from './RouteStatus.module.css';

type RouteErrorProps = {
  readonly error: unknown;
  readonly reset: () => void;
  readonly fullPage?: boolean;
  readonly overviewHref?: string;
};

function ErrorContent({
  error,
  reset,
  overviewHref,
}: Pick<RouteErrorProps, 'error' | 'reset' | 'overviewHref'>): ReactElement {
  const { t } = useTranslation(['app', 'common']);
  const headingId = useId();
  const presented = errorPresentation(error);
  const hasTechnicalDetails =
    presented.message !== null ||
    presented.status !== null ||
    presented.code !== null ||
    presented.correlationId !== null;

  return (
    <section className={styles.errorCard} role="alert" aria-labelledby={headingId}>
      <div className={styles.errorIcon} aria-hidden="true">
        <ScaleIconAlertError decorative size={28} />
      </div>

      <div className={styles.errorCopy}>
        <p className={styles.eyebrow}>{t('app:route.eyebrow')}</p>
        <h1 className={styles.heading} id={headingId}>
          {t(`app:route.${presented.kind}.title`)}
        </h1>
        <p className={styles.description}>{t(`app:route.${presented.kind}.description`)}</p>
      </div>

      <div className={styles.actions}>
        <ScaleButton variant="primary" onClick={reset}>
          <ScaleIconActionRefresh decorative size={18} slot="icon-before" />
          {t('common:actions.retry')}
        </ScaleButton>
        {overviewHref ? (
          <a className={styles.overviewLink} href={overviewHref}>
            {t('app:route.backToOverview')}
          </a>
        ) : null}
      </div>

      {hasTechnicalDetails ? (
        <details className={styles.details}>
          <summary>{t('app:route.technicalDetails')}</summary>
          <dl>
            {presented.message ? (
              <div>
                <dt>{t('app:route.detail.message')}</dt>
                <dd>{presented.message}</dd>
              </div>
            ) : null}
            {presented.status !== null ? (
              <div>
                <dt>{t('app:route.detail.status')}</dt>
                <dd>{presented.status}</dd>
              </div>
            ) : null}
            {presented.code ? (
              <div>
                <dt>{t('app:route.detail.code')}</dt>
                <dd>
                  <code>{presented.code}</code>
                </dd>
              </div>
            ) : null}
            {presented.correlationId ? (
              <div>
                <dt>{t('app:route.detail.correlationId')}</dt>
                <dd>
                  <code>{presented.correlationId}</code>
                </dd>
              </div>
            ) : null}
          </dl>
        </details>
      ) : null}
    </section>
  );
}

function FullPageFrame({ children }: { readonly children: ReactNode }): ReactElement {
  const { t } = useTranslation('app');

  return (
    <main className={styles.errorPage}>
      <div className={styles.brand}>
        <ScaleLogo
          className={styles.brandLogo}
          href="/"
          logoTitle={t('route.backToOverview')}
          size={30}
        />
        <span>
          <strong>CloudOrder</strong>
          <small>{t('tagline')}</small>
        </span>
      </div>
      <div className={styles.fullPageContent}>{children}</div>
    </main>
  );
}

/** Shown when a route throws. Keeps the shell intact whenever it is still available. */
export function RouteError({
  error,
  reset,
  fullPage = false,
  overviewHref,
}: RouteErrorProps): ReactElement {
  const content = (
    <ErrorContent
      error={error}
      reset={reset}
      {...(overviewHref === undefined ? {} : { overviewHref })}
    />
  );

  if (fullPage) {
    return <FullPageFrame>{content}</FullPageFrame>;
  }

  return <div className={styles.centred}>{content}</div>;
}
